import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Search, Users, Star } from 'lucide-react'
import { useWorkers } from '@/hooks/data/useWorkers'
import type { Worker } from '@/types/domain'

type VerificationStatus = NonNullable<Worker['verificationStatus']>

const STATUSES: VerificationStatus[] = ['NotSubmitted', 'Pending', 'Verified', 'Rejected']

function statusColor(s: VerificationStatus) {
  return (
    {
      NotSubmitted: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      Verified: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    }[s] ?? 'bg-gray-100 text-gray-600'
  )
}

function WorkersPage() {
  const [includeInactive, setIncludeInactive] = useState(false)
  const [statusFilter, setStatusFilter] = useState<VerificationStatus | ''>('')
  const [search, setSearch] = useState('')
  const { data, isLoading, isError, error } = useWorkers(includeInactive)

  const query = search.trim().toLowerCase()
  const filtered = (data ?? []).filter(
    (w) =>
      (!statusFilter || w.verificationStatus === statusFilter) &&
      (!query || w.name.toLowerCase().includes(query) || (w.email ?? '').toLowerCase().includes(query)),
  )

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Users className="w-6 h-6" /> Workers
        </h1>
        <p className="text-gray-600 dark:text-gray-400">Worker oversight — verification, activation, performance.</p>
      </div>

      <div className="relative max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white py-2 pl-9 pr-3 text-sm"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setStatusFilter('')}
          className={`px-3 py-1.5 rounded-lg text-sm ${!statusFilter ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}
        >
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}
          >
            {s}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : isError ? (
          <p className="p-4 text-sm text-red-600">Failed to load: {(error as Error)?.message}.</p>
        ) : filtered.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No workers match.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Rate</th>
                <th className="px-4 py-2">Rating</th>
                <th className="px-4 py-2">Verification</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 dark:text-white">{w.name}</p>
                    <p className="text-xs text-gray-400">{w.email ?? '—'}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    {w.hourlyRate != null ? `$${w.hourlyRate}/hr` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {w.averageRating != null ? (
                      <span className="flex items-center gap-1 text-gray-700 dark:text-gray-200">
                        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                        {w.averageRating.toFixed(1)} <span className="text-gray-400">({w.ratingCount})</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor(w.verificationStatus ?? 'NotSubmitted')}`}>
                      {w.verificationStatus ?? 'NotSubmitted'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={w.active ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                      {w.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to="/workers/$id"
                      params={{ id: w.id }}
                      className="text-blue-600 hover:underline dark:text-blue-400"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/workers/')({ component: WorkersPage })
